import { GridsterItemConfig } from 'angular-gridster2';

/**
 * Enum defining all available dashboard widget types.
 * Used to identify widgets for layout configuration and constraint mapping.
 *
 * Requirements: 7.1-7.6 (Widget-Specific Size Constraints)
 */
export enum DashboardWidgetType {
  CALENDAR = 'calendar',
  TASKS = 'tasks',
  EVICTIONS_BY_STEP = 'evictionsByStep',
  EVICTION_BREAKDOWN = 'evictionBreakdown',
  QUICK_LINKS = 'quickLinks',
  USAGE_SUMMARY = 'usageSummary',
  OPEN_EVICTIONS = 'openEvictions',
  CLIENT_STATUS = 'clientStatus',
  MAP = 'map',
  PROPERTY_MAP = 'propertyMap',
  CASES_CHART = 'casesChart',
  ATTORNEY_BREAKDOWN = 'attorneyBreakdown',
  RECENT_CASES = 'recentCases',
  // Summary card widgets
  INTAKE_EVICTIONS = 'intakeEvictions',
  OPEN_EVICTIONS_COUNT = 'openEvictionsCount',
  UNREAD_MESSAGES = 'unreadMessages',
  UNPAID_INVOICES = 'unpaidInvoices',
  PENDING_CHECKS = 'pendingChecks',
  // Property manager dashboard summary cards — distinct types (not CLIENT_STATUS) so each is
  // independently addressable in the edit toolbar's hide/show-by-type flow.
  CASES_AWAITING_SUBMISSION = 'casesAwaitingSubmission',
  OUTSTANDING_BALANCE = 'outstandingBalance'
}

/**
 * Interface defining size constraints for dashboard widgets.
 * Each widget type has minimum and optional maximum dimensions.
 *
 * Requirements: 3.2, 3.3 (Widget Resizing constraints)
 */
export interface WidgetConstraints {
  minCols: number;
  minRows: number;
  maxCols?: number;
  maxRows?: number;
}

/**
 * Extended GridsterItemConfig interface for dashboard widgets.
 * Includes widget identification and size constraints.
 */
export interface DashboardGridsterItem extends GridsterItemConfig {
  id: string;
  widgetType: DashboardWidgetType;
  visible?: boolean;
}

/**
 * Interface representing a single widget's layout position and size.
 * Used for persisting and restoring dashboard layouts.
 *
 * Requirements: 4.4 (Layout Configuration stores positions and sizes)
 */
export interface WidgetLayoutItem {
  id: string;        // Widget identifier (e.g., 'calendar', 'tasks', 'evictionsByStep')
  cols: number;      // Width in grid columns
  rows: number;      // Height in grid rows
  x: number;         // X position (column)
  y: number;         // Y position (row)
  visible?: boolean; // Whether the widget is visible on the dashboard (defaults to true if not specified)
}

/**
 * Interface representing the complete dashboard layout configuration.
 * Contains all widget positions/sizes and a version for future migrations.
 *
 * Requirements: 4.4 (Layout Configuration stores positions and sizes)
 */
export interface DashboardLayoutConfig {
  widgets: WidgetLayoutItem[];
  version: number; // For future migration support
}
